"use client";

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { clearTokens, getAccessToken, getMe, logout as apiLogout, type Profile } from "./api";

interface AuthContextValue {
  user: Profile | null;
  loading: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function chargerUtilisateurCourant(): Promise<Profile | null> {
  if (!getAccessToken()) return null;
  try {
    return await getMe();
  } catch {
    // Token invalide/expiré et non rafraîchissable : on repart d'une session propre.
    clearTokens();
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    setUser(await chargerUtilisateurCourant());
  }, []);

  useEffect(() => {
    let annule = false;
    chargerUtilisateurCourant().then((profile) => {
      if (annule) return;
      setUser(profile);
      setLoading(false);
    });
    return () => {
      annule = true;
    };
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, loading, refreshUser, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth doit être utilisé à l'intérieur d'un AuthProvider.");
  }
  return context;
}
